import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/auth';
import { 
  initializeOneSignal, 
  setOneSignalUserId, 
  setUserTags,
  areNotificationsEnabled 
} from '@/services/notifications/oneSignalService';
import { createLogger } from '@/services/utils/loggingUtils';

const logger = createLogger('useOneSignal');

export const useOneSignal = () => {
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const setupRunRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  const setupOneSignal = useCallback(async () => {
    // Prevent multiple setups for the same user
    if (setupRunRef.current && userIdRef.current === user?.id) {
      return;
    }

    setupRunRef.current = true;
    userIdRef.current = user?.id || null;

    try {
      const initialized = await initializeOneSignal();
      setIsInitialized(initialized);
      
      if (initialized) {
        // Check notification status with a brief delay to ensure OneSignal is ready
        setTimeout(() => {
          const notificationsEnabled = areNotificationsEnabled();
          setNotificationsEnabled(notificationsEnabled);
        }, 1000);

        // Set user ID if logged in and get player ID
        if (user?.id) {
          // Add delay for Safari PWA to ensure proper initialization
          const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const isPWA = window.matchMedia('(display-mode: standalone)').matches;
          const delay = (isMobileSafari || isPWA) ? 3000 : 1000;
          
          setTimeout(async () => {
            try {
              const playerId = await setOneSignalUserId(user.id);
              
              // Set user tags for segmentation
              await setUserTags({
                userId: user.id,
                userType: 'authenticated',
              });
              
              // Recheck notification status after user setup
              const finalEnabled = areNotificationsEnabled();
              setNotificationsEnabled(finalEnabled);
              
              logger.info('OneSignal setup completed', { playerId, notificationsEnabled: finalEnabled });
            } catch (userSetupError) {
              logger.error('Error in delayed user setup:', userSetupError);
            }
          }, delay);
        }
      }
    } catch (error) {
      logger.error('Error setting up OneSignal:', error);
      setupRunRef.current = false; // Allow retry
    }
  }, [user?.id]);

  useEffect(() => {
    // Reset setup flag when user changes
    if (userIdRef.current !== user?.id) {
      setupRunRef.current = false;
    }
    
    setupOneSignal();
  }, [setupOneSignal]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setupRunRef.current = false;
      userIdRef.current = null;
    };
  }, []);

  return {
    isInitialized,
    notificationsEnabled,
  };
};